import { workAreaQualifiedLabel } from '@featit/shared/addressing/workAreaRouteRef';
import {
  PHONE_SWITCHER_ORDER,
  type IProject,
  type PhoneSwitcherOrderType,
} from '@featit/shared/types/index';
import { DEFAULT_LOCALE, intlLocale, type AnyLocaleType } from '@featit/shared/i18n/core';

export type SwitcherChoiceWorkAreaType = Pick<
  IProject,
  'id' | 'name' | 'namespace' | 'namespaceName'
>;

const switcherCollators = new Map<string, Intl.Collator>();

/** One collator per language tag: "Area 2" before "Area 10", case and accents aside (IN-6). */
function switcherCollator(locale: AnyLocaleType): Intl.Collator {
  const tag = intlLocale(locale);
  let collator = switcherCollators.get(tag);
  if (!collator) {
    collator = new Intl.Collator(tag, { numeric: true, sensitivity: 'base' });
    switcherCollators.set(tag, collator);
  }
  return collator;
}

/**
 * F-0532 (AC-19.18): the work areas in the order `order` names, and the recent ones to rank. The
 * sheet draws Recent only for a request that ranks one, so "Custom order only" and "Alphabetical"
 * rank none. Recent first ranks the ones this person opened here, and only those (I-1992,
 * AC-19.21.3): `current` is not ranked when nothing is recent yet, since a current work area nobody
 * opened is the fallback a load took, and Recent then read as the first work area of the list.
 *
 * ⭐ T-0629 (AC-19.18.4): the one ordering. The native sheet's request (`workAreaSwitcherMessage`)
 * and the web's own pickers (`workAreaPickerOrder`) both call it, so the setting cannot mean one
 * order on the iPhone and another in a browser.
 */
export function workAreaSwitcherOrder<T extends SwitcherChoiceWorkAreaType>(
  order: PhoneSwitcherOrderType,
  projects: readonly T[],
  recentIds: readonly string[],
  current: string,
): { readonly workAreas: readonly T[]; readonly ranked: readonly string[] } {
  return workAreaSwitcherOrderIn(DEFAULT_LOCALE, order, projects, recentIds, current);
}

/** {@link workAreaSwitcherOrder}, "Alphabetical" sorted in `locale`'s order (IN-6). */
export function workAreaSwitcherOrderIn<T extends SwitcherChoiceWorkAreaType>(
  locale: AnyLocaleType,
  order: PhoneSwitcherOrderType,
  projects: readonly T[],
  recentIds: readonly string[],
  _current: string,
): { readonly workAreas: readonly T[]; readonly ranked: readonly string[] } {
  if (order === PHONE_SWITCHER_ORDER.CUSTOM) return { workAreas: projects, ranked: [] };
  if (order === PHONE_SWITCHER_ORDER.ALPHABETICAL) {
    // By what the row says, "Namespace / Work area" (AC-19.13), so the list reads sorted.
    const workAreas = [...projects].sort((a, b) =>
      switcherCollator(locale).compare(workAreaQualifiedLabel(a), workAreaQualifiedLabel(b)),
    );
    return { workAreas, ranked: [] };
  }
  const ranks = recentIds.some((id) => projects.some((project) => project.id === id));
  return { workAreas: projects, ranked: ranks ? recentIds : [] };
}
